import React, { useState, useEffect, useCallback } from 'react';
import { Input, Button, Spin } from 'antd';
import styled from 'styled-components';

const FullScreenContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background-color: black;
  color: white;
  flex-direction: column;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
`;

export function Password(props) {
  const [inputValue, setInputValue] = useState('');
  const [isVisible, setIsVisible] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingStored, setIsCheckingStored] = useState(false);

  const validateStoredPassword = useCallback(async (password) => {
    setIsCheckingStored(true);
    try {
      // Use same URL logic as handleSubmit
      let functionUrl;
      if (process.env.NODE_ENV === 'development') {
        // In development, use explicit localhost:8888 (Netlify dev port)
        functionUrl = 'http://localhost:8888/.netlify/functions/validatePassword';
      } else {
        // In production, use relative path (works when deployed to Netlify)
        functionUrl = `/.netlify/functions/validatePassword`;
      }

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.valid) {
        // Backend returns true: password is valid, hide the form
        setIsVisible(false);
      } else {
        // Backend returns false: password is invalid, clear it and show the form
        localStorage.removeItem('password');
        setIsCheckingStored(false);
      }
    } catch (error) {
      console.error('Error validating stored password:', error);
      // On error, clear stored password and show the form
      localStorage.removeItem('password');
      setIsCheckingStored(false);
    }
    // Note: if data.valid is true, we don't set isCheckingStored to false
    // because setIsVisible(false) will cause the component to return null
  }, []);

  useEffect(() => {
    // Check and validate stored password on mount
    const storedPassword = localStorage.getItem('password');
    if (storedPassword) {
      // If password exists in localStorage, validate it with backend
      validateStoredPassword(storedPassword);
    } else {
      // No stored password, show form immediately
      setIsCheckingStored(false);
    }
  }, [validateStoredPassword]);

  const handleSubmit = async () => {
    if (!inputValue.trim()) {
      return;
    }

    setIsLoading(true);
    try {
      // Construct the API URL - use REACT_APP_API_URL if set, otherwise use relative path
      const apiUrl = process.env.REACT_APP_API_URL || '';

      // In development, detect if we're on Netlify dev port (8888) or regular React port
      const currentPort = window.location.port ? parseInt(window.location.port) :
        (window.location.protocol === 'https:' ? 443 : 80);
      const isNetlifyDev = currentPort === 8888;
      const currentHost = window.location.hostname;
      const currentProtocol = window.location.protocol;


      // Construct the function URL
      // In production (deployed), always use relative path
      // In development, use localhost:8888 if not on that port already
      let functionUrl;
      if (process.env.NODE_ENV === 'development') {
        // In development, use explicit localhost:8888 (Netlify dev port)
        functionUrl = 'http://localhost:8888/.netlify/functions/validatePassword';
      } else {
        // In production, always use relative path (works when deployed to Netlify)
        // This will resolve to the same origin as the deployed site
        functionUrl = `/.netlify/functions/validatePassword`;
      }


      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      // Try the primary URL, with fallback to relative path if needed
      let response;
      try {
        response = await fetch(functionUrl, {
          method: 'POST',
          mode: 'cors', // Explicitly enable CORS
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ password: inputValue }),
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
      } catch (fetchError) {
        clearTimeout(timeoutId);

        // If fetch fails, the error will be thrown and handled below
        // No fallback needed since we're already using the correct URL
        throw fetchError;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.valid) {
        localStorage.setItem('password', inputValue);
        setIsVisible(false);
      } else {
        setHasError(true);
        setTimeout(() => {
          setHasError(false);
        }, 500);
      }
    } catch (error) {
      setHasError(true);
      setTimeout(() => {
        setHasError(false);
      }, 500);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isVisible) {
    return null; // Do not render anything if not visible
  }

  if (isCheckingStored) {
    return (
      <FullScreenContainer className='_fcc _fv _fgap2'>
        <Spin size="large" />
      </FullScreenContainer>
    );
  }

  return (
    <FullScreenContainer className='_fcc _fv _fgap2'
    >
      <>
        <h1>Password</h1>


        <div className="_f">
          <Input
            type="password"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onPressEnter={handleSubmit}
            placeholder="Enter password"
            size="large"
            style={{
              borderRadius: '1px',
              width: '200px',
              marginBottom: '20px',
              backgroundColor: hasError ? 'red' : 'white',
              color: 'black',
              transition: 'background-color 0.1s ease'
            }}
          />
          <Button
            type="primary"
            size="large"
            onClick={handleSubmit}
            loading={isLoading}>
            Submit
          </Button>
        </div>
      </>
    </FullScreenContainer>
  );
}
